import { useEffect, useState } from 'react';
import { Box, Card, Text, Group, AspectRatio, UnstyledButton, Tooltip } from '@mantine/core';
import {
  IconEye,
  IconStar,
  IconStarFilled,
  IconShare,
  IconEdit,
  IconTrash,
  IconTag,
  IconArrowsDiff,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { IProduct } from '@/types/Product.model';
import { IPopulatedListing } from '@/types/Listing.model';
import { cdnCompanyLogos, websiteDomain } from '../ApiPaths';
import { ProductListPreviewPicture } from './ProductListPreviewPicture';
import { UserProfile } from '@/store/Store';
import { isMobile } from 'is-mobile';
import {
  PRODUCT_CARD_PREVIEW_BG,
  PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT,
} from '@/constants/productPreview';
import { ListingStatusBadge, ArticleNumberBadge, ProductCardOverlayChip, VisibilityBadge } from './badges';
// Temporarily hidden — categories UI disabled
// import { CategoryBadge } from './badges';
import { useProductStarToggle, type ProductStarState } from '@/hooks/useProductStarToggle';
import { ProductStarLoginModal } from '@/components/ProductStarLoginModal';
import { colorWithAlpha } from '@/utils/colorWithAlpha';
import { DEFAULT_QR_CODE_COLOR } from '@/theme';
import {
  MAX_COMPARE,
  toCompareSelectionItem,
  useCompareStore,
} from '@/store/compareStore';
import { useTranslation } from 'react-i18next';
import classes from './ProductCard.module.css';

interface ProductCardProps {
  product: Partial<Omit<IProduct, 'owningUser_id'>> & {
    listings?: IPopulatedListing[];
    owningUser_id?: Partial<UserProfile>;
  };
  isLayout?: boolean;
  onClick?: () => void;
  showActions?: boolean;
  corporateColor?: string;
  onShareLink?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  hideOwnerInfo?: boolean;
  showDeleteOnly?: boolean;
  showArticleNumber?: boolean;
  showListingStatus?: boolean;
  showPrice?: boolean;
  /** Owner surfaces only (dashboard): show a "not public" badge when isPublic === false. */
  showVisibility?: boolean;
  onStarChange?: (productId: string, state: ProductStarState) => void;
}

export function ProductCard({
  product,
  isLayout = false,
  onClick,
  showActions = false,
  corporateColor = DEFAULT_QR_CODE_COLOR,
  onShareLink,
  onEdit,
  onDelete,
  hideOwnerInfo = false,
  showDeleteOnly = false,
  showArticleNumber = true,
  showListingStatus = true,
  showPrice = true,
  showVisibility = false,
  onStarChange,
}: ProductCardProps) {
  const { t } = useTranslation();
  const mobile = isMobile();
  const [hovered, setHovered] = useState(false);

  const {
    starCount,
    starredByMe,
    starActionLoading,
    starActionTooltip,
    starLoginModalOpened,
    setStarLoginModalOpened,
    handleToggleStar,
    confirmStarLogin,
  } = useProductStarToggle({
    productId: product._id,
    initialStarCount: product.starCount ?? 0,
    initialStarredByMe: Boolean(product.starredByMe),
    onStarChange,
  });

  const showStarBadge = !isLayout;
  const showCompare = !isLayout && Boolean(product._id);
  const productId = product._id;
  // Subscribe to booleans, not the whole items array, so unrelated cards stay put.
  const compareSelected = useCompareStore((s) =>
    Boolean(productId && s.items.some((item) => item._id === productId))
  );
  const compareFull = useCompareStore(
    (s) => s.items.length >= MAX_COMPARE && !s.items.some((item) => item._id === productId)
  );

  const handleCompareClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    const item = toCompareSelectionItem(product);
    if (!item) return;
    const { items, toggle } = useCompareStore.getState();
    const trayFull = items.length >= MAX_COMPARE && !items.some((entry) => entry._id === item._id);
    if (trayFull) {
      notifications.show({
        color: 'orange',
        message: t('marketplace.compare.maxReached', { max: MAX_COMPARE }),
      });
      return;
    }
    toggle(item);
  };

  const showPriceBadge =
    !isLayout &&
    showPrice &&
    product._id &&
    product.metadata?.rows?.[product._id]?.data?.price;

  const previewHeight = mobile
    ? PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT.mobile
    : PRODUCT_CARD_PREVIEW_IMAGE_HEIGHT.desktop;

  const borderColor = hovered
    ? colorWithAlpha(corporateColor, 0.55)
    : 'var(--mantine-color-default-border)';
  const actionsBarBackground = colorWithAlpha(corporateColor, hovered ? 0.07 : 0.04);

  const owner = product.owningUser_id;
  const ownerInitial =
    owner?.companyName?.trim().charAt(0).toUpperCase() ||
    owner?.username?.trim().charAt(0).toUpperCase() ||
    '?';
  const ownerLogoSrc = owner?.companyLogo ? `${cdnCompanyLogos}${owner.companyLogo}` : null;
  const [ownerLogoFailed, setOwnerLogoFailed] = useState(false);

  useEffect(() => {
    setOwnerLogoFailed(false);
  }, [ownerLogoSrc]);

  return (
    <Card
      shadow={hovered ? 'md' : 'sm'}
      padding={0}
      radius="md"
      withBorder
      className={`${classes.card} ${mobile ? classes.cardMobile : classes.cardDesktop}`}
      style={{
        borderColor,
        backgroundColor: 'var(--mantine-color-default)',
        ['--product-card-accent' as string]: corporateColor,
      }}
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <Card.Section>
        <AspectRatio ratio={1} style={{ height: previewHeight }}>
          <div className={classes.preview} style={{ backgroundColor: PRODUCT_CARD_PREVIEW_BG }}>
            <div className={classes.previewImageLayer}>
              {product._id && (isLayout || product.productImage) ? (
                <ProductListPreviewPicture
                  entityId={product._id}
                  isLayout={isLayout}
                  productImage={product.productImage}
                  imageVersion={!isLayout ? product.imageVersion : undefined}
                  previewImageAccess={!isLayout ? product.previewImageAccess : undefined}
                  alt={
                    isLayout
                      ? product.productName || 'Layout image'
                      : product.productName || 'Product image'
                  }
                  fit="contain"
                />
              ) : null}
            </div>

            <div className={classes.overlayTopBar}>
              {showPriceBadge && product._id ? (
                <ProductCardOverlayChip variant="price" icon={<IconTag size={9} stroke={2} />}>
                  {product.metadata?.rows?.[product._id]?.data?.price}
                </ProductCardOverlayChip>
              ) : null}

              <div className={classes.overlayTopEnd}>
                <ProductCardOverlayChip variant="views" icon={<IconEye size={9} stroke={2} />}>
                  {product.views || 0}
                </ProductCardOverlayChip>
              </div>
            </div>
          </div>
        </AspectRatio>
      </Card.Section>

      <Box className={classes.footer}>
        <div className={classes.titleBlock}>
          <div className={classes.titleRow}>
            <Text fw={600} size="sm" lineClamp={1} className={classes.productTitle}>
              {product.productName}
            </Text>

            {(showStarBadge || showCompare) && (
              <div className={classes.titleActions}>
                {showCompare ? (
                  <Tooltip
                    label={
                      compareSelected
                        ? t('marketplace.compare.remove')
                        : t('marketplace.compare.compareAction')
                    }
                  >
                    <div onClick={(e) => e.stopPropagation()}>
                      <ProductCardOverlayChip
                        variant={compareSelected ? 'starred' : 'star'}
                        icon={<IconArrowsDiff size={10} stroke={2} />}
                        interactive
                        ariaLabel={
                          compareSelected
                            ? t('marketplace.compare.remove')
                            : t('marketplace.compare.compareAction')
                        }
                        onClick={handleCompareClick}
                      >
                        {''}
                      </ProductCardOverlayChip>
                    </div>
                  </Tooltip>
                ) : null}
                {showStarBadge ? (
                  <Tooltip label={starActionTooltip}>
                    <div>
                      <ProductCardOverlayChip
                        variant={starredByMe ? 'starred' : 'star'}
                        icon={
                          starredByMe ? (
                            <IconStarFilled size={10} />
                          ) : (
                            <IconStar size={10} stroke={2} />
                          )
                        }
                        interactive
                        loading={starActionLoading}
                        disabled={!product._id}
                        ariaLabel={starActionTooltip}
                        onClick={(event) => void handleToggleStar(event)}
                      >
                        {starCount > 0 ? starCount : ''}
                      </ProductCardOverlayChip>
                    </div>
                  </Tooltip>
                ) : null}
              </div>
            )}
          </div>

          {!isLayout && (showArticleNumber || showListingStatus || showVisibility) && (
            <div className={classes.metaRow}>
              {showVisibility && (
                <VisibilityBadge
                  isPublic={product.isPublic}
                  linkShareMode={product.linkShareMode}
                  appearance="card"
                  iconOnly
                />
              )}

              {/* Temporarily hidden — categories UI disabled
              {product.category ? (
                <CategoryBadge category={product.category} appearance="card" />
              ) : null}
              */}

              {showArticleNumber && product.articleNumber && (
                <ArticleNumberBadge articleNumber={product.articleNumber} appearance="card" />
              )}

              {showListingStatus && (
                <ListingStatusBadge listings={product.listings} appearance="card" />
              )}
            </div>
          )}
        </div>

        <div className={classes.bottomSection}>
          {!hideOwnerInfo &&
            (showActions ? (
              <div
                className={`${classes.actionsBar} ${showDeleteOnly ? classes.actionsBarDeleteOnly : ''}`}
                style={{ backgroundColor: actionsBarBackground }}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                {!showDeleteOnly && (
                  <>
                    {onShareLink ? (
                      <Tooltip label={t('dashboard.actions.shareLink')}>
                        <UnstyledButton
                          className={classes.actionButton}
                          aria-label={t('dashboard.actions.shareLink')}
                          onClick={(e: React.MouseEvent) => {
                            e.stopPropagation();
                            onShareLink();
                          }}
                        >
                          <Group justify="center" h={32}>
                            <IconShare size={17} stroke={1.75} />
                          </Group>
                        </UnstyledButton>
                      </Tooltip>
                    ) : null}

                    <Tooltip label={t('dashboard.actions.edit')}>
                      <UnstyledButton
                        className={classes.actionButton}
                        aria-label={t('dashboard.actions.edit')}
                        onClick={(e: React.MouseEvent) => {
                          e.stopPropagation();
                          onEdit?.();
                        }}
                      >
                        <Group justify="center" h={32}>
                          <IconEdit size={17} stroke={1.75} />
                        </Group>
                      </UnstyledButton>
                    </Tooltip>
                  </>
                )}

                <Tooltip label={t('dashboard.actions.delete')}>
                  <UnstyledButton
                    className={classes.actionButton}
                    style={showDeleteOnly ? { flex: '0 0 auto', width: 40 } : undefined}
                    aria-label={t('dashboard.actions.delete')}
                    onClick={(e: React.MouseEvent) => {
                      e.stopPropagation();
                      onDelete?.();
                    }}
                  >
                    <Group justify="center" h={32}>
                      <IconTrash size={17} stroke={1.75} />
                    </Group>
                  </UnstyledButton>
                </Tooltip>
              </div>
            ) : (
              <UnstyledButton
                className={classes.ownerRow}
                style={{ width: '100%' }}
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation();
                  if (product.owningUser_id?.username) {
                    window.location.href = `${websiteDomain}/user/${product.owningUser_id.username}`;
                  }
                }}
              >
                <div className={classes.ownerMark} aria-hidden>
                  {ownerLogoSrc && !ownerLogoFailed ? (
                    <img
                      src={ownerLogoSrc}
                      alt=""
                      className={classes.ownerLogo}
                      loading="lazy"
                      decoding="async"
                      onError={() => setOwnerLogoFailed(true)}
                    />
                  ) : (
                    <span className={classes.ownerInitial}>{ownerInitial}</span>
                  )}
                </div>
                <Box className={classes.ownerText}>
                  <Text size="xs" fw={700} lineClamp={1} style={{ lineHeight: 1.2 }}>
                    {product.owningUser_id?.companyName}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1} style={{ lineHeight: 1.2 }}>
                    @{product.owningUser_id?.username}
                  </Text>
                </Box>
              </UnstyledButton>
            ))}
        </div>
      </Box>

      <ProductStarLoginModal
        opened={starLoginModalOpened}
        onClose={() => setStarLoginModalOpened(false)}
        onConfirm={confirmStarLogin}
        accentColor={corporateColor}
      />
    </Card>
  );
}
